import { animate, useInView } from "framer-motion";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { usePrefersReducedMotion } from "@/components/home/usePrefersReducedMotion";

/**
 * ReactBits count-up 포팅 (home-ui-spec §3·§8). 뷰포트 진입 시 0→값 1회.
 * 개조: tabular-nums + 최종값 폭 min-width 선점(CLS 0), reduced-motion 시
 * 즉시 최종값(§7.4).
 */
export interface CountUpProps {
  /** 최종값 */
  to: number;
  /** 카운트 시간 ms (§3: 900) */
  duration?: number;
  /** 표기 포맷 — 기본 정수 로케일 그룹핑 */
  format?: (value: number) => string;
  className?: string;
}

const defaultFormat = (value: number) => Math.round(value).toLocaleString();

const hostStyle: CSSProperties = {
  position: "relative",
  display: "inline-block",
  fontVariantNumeric: "tabular-nums",
};

export function CountUp({
  to,
  duration = 900,
  format = defaultFormat,
  className,
}: CountUpProps) {
  const reduced = usePrefersReducedMotion();
  const hostRef = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);
  const inView = useInView(hostRef, { once: true });
  const [value, setValue] = useState(() => (reduced ? to : 0));

  useEffect(() => {
    if (!inView) return;
    if (reduced || startedRef.current) {
      startedRef.current = true;
      setValue(to);
      return;
    }
    startedRef.current = true;
    const controls = animate(0, to, {
      duration: duration / 1000,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: setValue,
    });
    return () => controls.stop();
  }, [inView, reduced, to, duration]);

  return (
    <span ref={hostRef} className={className} style={hostStyle}>
      {/* 최종값 폭 선점 — 자릿수 점프에 의한 레이아웃 시프트 방지(§3) */}
      <span aria-hidden style={{ visibility: "hidden" }}>
        {format(to)}
      </span>
      <span style={{ position: "absolute", inset: 0 }}>{format(value)}</span>
    </span>
  );
}
